// TODO
// - needs some consolidation between this and the InviteCard

import { get } from "lodash";
import moment from "moment-timezone";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Grid, Header, Card } from "semantic-ui-react";

import ResourceIcon from "../../resources/resourceIcon/ResourceIcon.jsx";
import SegmentMessage from "../../forms/messages/SegmentMessage.jsx";
import InviteResponseForm from "./InviteResponseForm.jsx";
import ResourceMarker from "../../marking/ResourceMarker.jsx";
import LinkWithReset from "../../utils/LinkWithReset.jsx";
import CopyString from "../../utils/CopyString.jsx";
import withBasicForm from "../../forms/withBasicForm.js";

import { MARKING_STATES } from "../../../utils/marking.js";
import { MODIFIED_AT_TIME_FORMAT } from "../../config.js";
import { OPERATIONS } from "../../../constants/operations.js";

import { selectCopy } from "../../../state/reducers/locale/copySelector.js";
import { AT_TYPES } from "../../../constants/entity.js";

const BasicInviteResponseForm = withBasicForm(InviteResponseForm);

class InvitePanel extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    invite: PropTypes.object.isRequired,
    onAccept: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    copy: PropTypes.func.isRequired
  };

  static INITIAL_VIEW = {
    message: null,
    disabled: false
  };

  constructor(props) {
    super(props);

    this.state = {
      isPending: true,
      isAccepted: false,
      isInvitee: false,
      isInviter: false,
      view: InvitePanel.INITIAL_VIEW,
      challengeResponse: null
    };
  }

  static getDerivedStateFromProps(props, prevProps) {
    if (props !== prevProps) {
      const { invite, user } = props;

      // casting toLowerCase as sherpa will store user data as entered, though won't allow case insensitive duplicates.
      const isInvitee =
        get(invite, "subject.email", "").toLowerCase() ===
        get(user, "email", "").toLowerCase();

      const isInviter =
        get(invite, "createdBy.@id", "").toLowerCase() ===
        get(user, "@id", "").toLowerCase();

      const isPending = invite.marking === MARKING_STATES.PENDING_SUBJECT;
      const isAccepted = invite.marking === MARKING_STATES.ACCEPTED;

      return { isInvitee, isInviter, isPending, isAccepted };
    }
  }

  // -- HANDLERS -- //

  onAccept = values => {
    const { invite, onAccept } = this.props;
    onAccept(invite, OPERATIONS.ACCEPT, values);
  };

  onReject = () => {
    const { invite, onReject } = this.props;
    onReject(invite, OPERATIONS.REJECT);
  };

  onDelete = () => {
    const { invite, onDelete } = this.props;
    onDelete(invite, OPERATIONS.DELETE);
  };

  // -- RENDER -- //

  getViewState = invite => {
    const { copy } = this.props;

    if (!invite) return InvitePanel.INITIAL_VIEW;

    switch (invite.marking) {
      case MARKING_STATES.PENDING_OBJECT:
        return {
          info: true,
          icon: "exclamation circle",
          message: copy(`invite.${invite.marking}.message`),
          disabled: true
        };
      case MARKING_STATES.PENDING_SUBJECT:
        return {
          icon: "exclamation circle",
          iconColor: "orange",
          message: this.getExpiryString(),
          disabled: true
        };
      case MARKING_STATES.ACCEPTED:
        return {
          positive: true,
          icon: "check circle",
          message: copy(`invite.${invite.marking}.message`),
          disabled: false
        };
      case MARKING_STATES.REJECTED:
        return {
          negative: true,
          icon: "times circle",
          message: copy(`invite.${invite.marking}.message`),
          disabled: true
        };
      case MARKING_STATES.EXPIRED:
        return {
          negative: true,
          icon: "exclamation circle",
          message: copy(`invite.${invite.marking}.message`),
          disabled: true
        };
      case MARKING_STATES.DELETED:
        return {
          negative: true,
          icon: "exclamation circle",
          message: copy(`invite.${invite.marking}.message`),
          disabled: true
        };

      default:
        return InvitePanel.INITIAL_VIEW;
    }
  };

  renderGreeting = () => {
    const { invite, copy } = this.props;
    const { isInvitee } = this.state;

    // if the current user isn't the inviter
    if (isInvitee) {
      return <Header>{copy("invite.invitee_greeting")}</Header>;
    }

    const inviteEmail = get(invite, "subject.email", null);
    return (
      <Header>
        <a href={`mailto:${inviteEmail}`}>{inviteEmail}</a>
        {` ${copy("invite.inviter_greeting")}`}
      </Header>
    );
  };

  renderSudo = link => {
    const { copy } = this.props;

    if (!link || typeof link.sudo !== "boolean") return null;

    const role =
      link.sudo === true
        ? copy(`invite.sudo_roles.admin`)
        : copy(`invite.sudo_roles.standard`);

    return (
      <p>
        <strong>{`${copy("invite.sudo_label")}: `}</strong>
        {role}
      </p>
    );
  };

  renderGroup = link => {
    const { copy } = this.props;

    const group = get(link, "group.name", null);

    if (!group) return null;

    return (
      <p>
        <strong>{`${copy("invite.group_label")}: `}</strong>
        {group}
      </p>
    );
  };

  renderProject = object => {
    const { copy } = this.props;
    const { isInviter, isAccepted } = this.state;

    const project = get(object, "project", null);

    if (!project) return null;

    if (isAccepted === true || isInviter === true) {
      return (
        <p>
          <strong>{`${copy("invite.project_label")}: `}</strong>
          <LinkWithReset to={project["@id"]}>{project.name}</LinkWithReset>
        </p>
      );
    }

    return (
      <p>
        <strong>{`${copy("invite.project_label")}: `}</strong>
        {project.name}
      </p>
    );
  };

  renderID = invite => {
    const { copy } = this.props;

    const createdAtStr = moment(invite.createdAt).format(
      MODIFIED_AT_TIME_FORMAT
    );

    return (
      <Fragment>
        <p>
          <strong>{`${copy("invite.created_at_label")}: `}</strong>
          {createdAtStr}
        </p>
        <p>
          <strong>{`${copy("invite.id_label")}: `}</strong>
          <CopyString copyText={invite.id} displayText={invite.id} />
        </p>
      </Fragment>
    );
  };

  getExpiryString = () => {
    const { invite, copy } = this.props;

    const expiresTime = moment(invite.expiredAt);

    const expiryKey = moment().isSameOrAfter(expiresTime)
      ? "expired"
      : "expires";

    return `${copy(`expiry_string.${expiryKey}`)}: ${expiresTime.format(
      MODIFIED_AT_TIME_FORMAT
    )}`;
  };

  renderHeader = () => {
    const { invite } = this.props;

    if (!invite) return null;

    const view = this.getViewState(invite);

    if (!view.message) return null;

    return (
      <SegmentMessage {...view} attached="top">
        <ResourceMarker
          marking={invite.marking}
          type={AT_TYPES.INVITE}
          className="float-right"
          size="massive"
        />
      </SegmentMessage>
    );
  };

  render = () => {
    const { invite } = this.props;
    const { isInviter, isPending, isAccepted } = this.state;

    const view = this.getViewState(invite);

    return (
      <Card key={invite["@id"]} fluid>
        {this.renderHeader()}
        <Card.Content disabled={view.disabled}>
          <Grid stackable padded columns="equal" verticalAlign="top">
            <Grid.Column stretched>
              {this.renderGreeting(invite)}
              <p style={{ marginTop: "1em", marginBottom: "2em" }}>
                <ResourceIcon
                  size="big"
                  data={invite.object}
                  disabled={!(isInviter || isAccepted)}
                />
              </p>
              {this.renderSudo(invite.link)}
              {this.renderProject(invite.object)}
              {this.renderGroup(invite.link)}
              {this.renderID(invite)}
            </Grid.Column>
            {isPending && (
              <BasicInviteResponseForm
                data={invite}
                onSubmit={this.onAccept}
                onDelete={this.onDelete}
                onReject={this.onReject}
                copyKey={"invite.pending"}
              />
            )}
          </Grid>
        </Card.Content>
      </Card>
    );
  };
}

const mapStateToProps = state => ({
  user: state.account.data,
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(InvitePanel);
